import React, { useState } from 'react';
import './LoadProgress.scss';
import { Query } from '../../_common';
import { Error, Loading } from '../../_common';
import GET_LOAD_PROGRESS from '../../../graphql/client/queries/getLoadProgress';
import {
    GetLoadProgress_getLoadProgress as LoadProgressData,
    GetLoadProgress,
    GetLoadDetail_getLoadDetail as LoadDetail,
} from '../../../types/schemaTypes';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { CustomButton } from '../../_common';
import EditLoadProgress from '../../EditLoad/EditLoadProgress';
import { Option } from '../../Users/interface';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import useMarkLoadComplete from './useMarkLoadComplete';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { DriverLoadStatusEnum } from '../../../types/DriverLoadStatusEnum';
import UploadLoadImage from './LoadImage/UploadLoadImage';
import EditLoadProgressValidationModal from '../../EditLoad/EditLoadProgress/EditLoadProgressValidationModal';
import CustomLoadInformationModal from './CustomLoadInformationModal';
import ForbiddenAccessModal from './ForbiddenAccessModal';

interface LoadProgressProps {
    loadId: number;
    loadDetail: LoadDetail;
}

const loadStatus: Option[] = [
    { id: 'Created', name: 'Created' },
    { id: 'Assigned', name: 'Assigned' },
    { id: 'Loading', name: 'Loading' },
    { id: 'Loaded', name: 'Loaded' },
    { id: 'On-site', name: 'On Site' },
    { id: 'Delivered', name: 'Delivered' },
];

const LoadProgress = ({ loadId, loadDetail }: LoadProgressProps): React.ReactElement => {
    const [showModal, setShowModal] = useState(false);
    const [loadProgress, setLoadProgress] = useState<any>();

    const handleOnClickHide = (): void => {
        setShowModal(false);
    };
    const variables = {
        loadId: loadId,
    };

    const timeConverter = (minutes: string | null): string => {
        if (minutes != null) {
            const hours = parseInt(minutes) / 60;
            const rhours = Math.floor(hours);
            const min = (hours - rhours) * 60;
            const rmin = Math.round(min);
            return rhours + 'h' + ' ' + rmin + 'm';
        } else {
            return '';
        }
    };

    const isComplete = (complete: string | null): boolean => {
        if (complete != '' && complete != null) {
            return true;
        }
        return false;
    };
    const authContext = useAuthContext();
    const canEditLoadProgress = authContext.userHasPermission(PermissionEnum.EditLoadProgress);
    const canMarkLoadComplete = authContext.userHasPermission(PermissionEnum.MarkLoadComplete);
    const canUploadImage = authContext.userHasPermission(PermissionEnum.UploadImages);
    const {
        handleSubmit,
        showErrorModal,
        loadingErrorMessage,
        loadedErrorMessage,
        onSiteErrorMessage,
        deliveredErrorMessage,
        sandTicketImageErrorMessage,
        bolTicketImageErrorMessage,
        sandTicketNumberErrorMessage,
        bolNumberErrorMessage,
        weightErrorMessage,
        milesToWellErrorMessage,
        statusErrorMessage,
        handleOnClickCancel,
        showCompletedModal,
        showForbiddenModal,
        otherTicketNumberErrorMessage,
        tmsIdErrorMessage,
    } = useMarkLoadComplete(loadId, handleOnClickHide, loadProgress, loadDetail);

    const checkForDriverInformation = (): boolean => {
        if(loadDetail.isBellyDump) return true;
        if (
            loadDetail.driverId === 0 ||
            loadDetail.driverId === null ||
            loadDetail.boxNumber === '' ||
            loadDetail.chassisNumber === '' ||
            loadDetail.truckNumber === ''
        ) {
            return false;
        }
        return true;
    };
    return (
        <Query<GetLoadProgress> query={GET_LOAD_PROGRESS} variables={variables} fetchPolicy="cache-and-network">
            {({ loading, error, data }): React.ReactElement | null => {
                React.useEffect(() => {
                    if (data && data.getLoadProgress) {
                        setLoadProgress(data.getLoadProgress as LoadProgressData);
                    }
                }, [data, data?.getLoadProgress]);

                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }



                if (data && data.getLoadProgress) {
                    const lp = data.getLoadProgress as LoadProgressData;

                    const sandTicketComponent = (
                        <UploadLoadImage
                            ticketNumber={lp.sandTicketNumber}
                            ticketImage={lp.sandTicketImage}
                            ticketUploadedBy={lp.sandTicketUploadedBy}
                            imageText="SandTicket #: "
                            ticketUploadedAt={lp.sandTicketUploadedAt}
                            ticketName="stn"
                            loadId={loadId}
                            loadStatus={loadDetail.status}
                            canUploadImage={canUploadImage}
                        />
                    );

                    const additionalTicketComponent = (
                        <UploadLoadImage
                            ticketNumber={lp.additionalTicketNumber}
                            ticketImage={lp.additionalTicketImage}
                            ticketUploadedBy={lp.additionalTicketUploadedBy}
                            imageText="Additional Sand Ticket/Other: "
                            ticketUploadedAt={lp.additionalTicketUploadedAt}
                            ticketName="bol2"
                            loadId={loadId}
                            loadStatus={loadDetail.status}
                            canUploadImage={canUploadImage}
                        />
                    );

                    const bolTicketComponent = (
                        <UploadLoadImage
                            ticketNumber={loadId.toString()}
                            ticketImage={lp.bolTicketImage}
                            ticketUploadedBy={lp.bolTicketUploadedBy}
                            imageText="Sandbox BOL #: "
                            ticketUploadedAt={lp.bolTicketUploadedAt}
                            ticketName="bol"
                            loadId={loadId}
                            loadStatus={loadDetail.status}
                            canUploadImage={canUploadImage}
                        />
                    );
                    return (
                        <div className="LoadProgress-Container">
                            {checkForDriverInformation() ? (
                                <div className="LoadProgress-EditLink">
                                    {canEditLoadProgress && (
                                        <Button
                                            className={`LoadProgress-Edit ${loadDetail.status ===
                                                LoadStatusEnum.CANCELED && 'LoadProgress-EditLink-Disabled'}`}
                                            variant="link"
                                            title="Test"
                                            onClick={(): void => setShowModal(true)}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    <EditLoadProgress
                                        onClickShow={() => setShowModal(true)}
                                        show={showModal}
                                        onClickHide={handleOnClickHide}
                                        loadStatus={loadStatus}
                                        loadProgress={lp}
                                        loadDetail={loadDetail}
                                    />
                                </div>
                            ) : (
                                <div
                                    className="LoadProgress-EditLink"
                                    title="Please enter Driver, Box, Chassis and Truck."
                                >
                                    {canEditLoadProgress && (
                                        <Button className="LoadProgress-Edit" variant="link">
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            )}

                            <Row>
                                <Col>
                                    <p className="LoadProgress-ElapsedTimeLabel"></p>
                                </Col>
                                <Col>
                                    <p className="LoadProgress-DeliveryTimeLabel"></p>
                                </Col>
                                <Col>
                                    <p className="LoadProgress-ElapsedTimeLabel">
                                        Wait Time: {timeConverter(lp.loadingElapsedTime)}{' '}
                                    </p>
                                </Col>
                                <Col>
                                    <p className="LoadProgress-MilesLabel">{lp.milesToWell} mile(s)</p>
                                </Col>
                                <Col>
                                    <p className="LoadProgress-DeliveryTimeLabel">
                                        Wait Time: {timeConverter(lp.deliveryElapsedTime)}{' '}
                                    </p>
                                </Col>
                                <Col>
                                    <p className="LoadProgress-DeliveryTimeLabel"></p>
                                </Col>
                            </Row>
                            <div className="step-wizard">
                                <div className="step-wizard-row setup-panel">
                                    <div className="step-wizard-step">
                                        <div className="icon-size">
                                            {loadDetail.driverId != 0 ? (
                                                loadDetail.driverLoadStatus === DriverLoadStatusEnum.ACCEPTED ? (
                                                    <i className="material-icons color LoadProgress-Icon">
                                                        check_circle_outline
                                                    </i>
                                                ) : (
                                                    <i className="material-icons color LoadProgress-OnIcon ">
                                                        brightness_1
                                                    </i>
                                                )
                                            ) : (
                                                <i className="material-icons color LoadProgress-NotCompletedIcon ">
                                                    brightness_1
                                                </i>
                                            )}
                                        </div>
                                        <p>Accepted</p>
                                        <p className="date"> {lp.acceptedDateTime}</p>
                                    </div>
                                    <div className="step-wizard-step">
                                        <div className="icon-size">
                                            {loadDetail.driverLoadStatus === DriverLoadStatusEnum.ACCEPTED ||
                                                lp.loadingDateTime !== '' ? (
                                                isComplete(lp.loadingDateTime) ? (
                                                    <i className="material-icons color LoadProgress-Icon">
                                                        check_circle_outline
                                                    </i>
                                                ) : (
                                                    <i className="material-icons color LoadProgress-OnIcon ">
                                                        brightness_1
                                                    </i>
                                                )
                                            ) : (
                                                <i className="material-icons color LoadProgress-NotCompletedIcon ">
                                                    brightness_1
                                                </i>
                                            )}
                                        </div>{' '}
                                        <p>Loading</p>
                                        <p className="date"> {lp.loadingDateTime}</p>
                                    </div>
                                    <div className="step-wizard-step">
                                        <div className="icon-size">
                                            {isComplete(lp.loadingDateTime) ? (
                                                isComplete(lp.loadedDateTime) ? (
                                                    <i className="material-icons color LoadProgress-Icon">
                                                        check_circle_outline
                                                    </i>
                                                ) : (
                                                    <i className="material-icons color LoadProgress-OnIcon ">
                                                        brightness_1
                                                    </i>
                                                )
                                            ) : (
                                                <i className="material-icons color LoadProgress-NotCompletedIcon ">
                                                    brightness_1
                                                </i>
                                            )}
                                        </div>{' '}
                                        <p>Loaded</p>
                                        <p className="date">{lp.loadedDateTime}</p>
                                        <p className="weight">
                                            <span className="EmptyBox-Bold">Weight (lbs):</span>{' '}
                                            {
                                                <NumberFormat
                                                    value={lp.weight || 0}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                />
                                            }
                                        </p>
                                        <p className="gross-weight">
                                            <span className="EmptyBox-Bold">Gross Weight (lbs):</span>{' '}
                                            {
                                                <NumberFormat
                                                    value={lp.grossWeight || 0}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                />
                                            }
                                        </p>
                                        <p className="tare-weight">
                                            <span className="EmptyBox-Bold">Tare Weight (lbs):</span>{' '}
                                            {
                                                <NumberFormat
                                                    value={lp.tareWeight || 0}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                />
                                            }
                                        </p>
                                    </div>
                                    <div className="step-wizard-step">
                                        <div className="icon-size">
                                            {isComplete(lp.loadedDateTime) ? (
                                                isComplete(lp.onSiteDateTime) ? (
                                                    <i className="material-icons color LoadProgress-Icon">
                                                        check_circle_outline
                                                    </i>
                                                ) : (
                                                    <i className="material-icons color LoadProgress-OnIcon ">
                                                        brightness_1
                                                    </i>
                                                )
                                            ) : (
                                                <i className="material-icons color LoadProgress-NotCompletedIcon ">
                                                    brightness_1
                                                </i>
                                            )}
                                        </div>{' '}
                                        <p>On-Site</p>
                                        <p className="date">{lp.onSiteDateTime}</p>
                                    </div>
                                    <div className="step-wizard-step">
                                        <div className="icon-size">
                                            {isComplete(lp.onSiteDateTime) ? (
                                                isComplete(lp.deliveredDateTime) ? (
                                                    <i className="material-icons color LoadProgress-Icon">
                                                        check_circle_outline
                                                    </i>
                                                ) : (
                                                    <i className="material-icons color LoadProgress-OnIcon ">
                                                        brightness_1
                                                    </i>
                                                )
                                            ) : (
                                                <i className="material-icons color LoadProgress-NotCompletedIcon ">
                                                    brightness_1
                                                </i>
                                            )}
                                        </div>{' '}
                                        <p>Delivered</p>
                                        <p className="date">{lp.deliveredDateTime}</p>
                                        {lp.signerName ? (
                                            <p className="Signer-Name">
                                                <span className="EmptyBox-Bold">Signer:</span>{' '}
                                                {lp.signerName || ''}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                        {lp.emptyBox1 ? (
                                            <p className="EmptyBox-1">
                                                <span className="EmptyBox-Bold">Empty Box 1 #:</span>{' '}
                                                {lp.emptyBox1 || ''}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                        {lp.emptyBox2 ? (
                                            <p className="EmptyBox-2">
                                                <span className="EmptyBox-Bold">Empty Box 2 #:</span>{' '}
                                                {lp.emptyBox2 || ''}
                                            </p>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="LoadProgress-Images">
                                {sandTicketComponent}
                                {additionalTicketComponent}
                                {bolTicketComponent}
                            </div>
                            {loadDetail.status === LoadStatusEnum.DELIVERED && canMarkLoadComplete && (
                                <CustomButton
                                    additionalStyle="LoadProgress-Mark-Complete"
                                    title="Mark Load Complete"
                                    onClick={handleSubmit}
                                ></CustomButton>
                            )}
                            <EditLoadProgressValidationModal
                                show={showErrorModal}
                                onCancel={handleOnClickCancel}
                                loadingErrorMessage={loadingErrorMessage}
                                loadedErrorMessage={loadedErrorMessage}
                                onSiteErrorMessage={onSiteErrorMessage}
                                deliveredErrorMessage={deliveredErrorMessage}
                                sandTicketImageErrorMessage={sandTicketImageErrorMessage}
                                bolTicketImageErrorMessage={bolTicketImageErrorMessage}
                                sandTicketNumberErrorMessage={sandTicketNumberErrorMessage}
                                bolNumberErrorMessage={bolNumberErrorMessage}
                                weightErrorMessage={weightErrorMessage}
                                milesToWellErrorMessage={milesToWellErrorMessage}
                                fromMarkLoad={true}
                                statusErrorMessage={statusErrorMessage}
                                otherTicketNumberErrorMessage={otherTicketNumberErrorMessage}
                                tmsIdErrorMessage={tmsIdErrorMessage}
                            />
                            <CustomLoadInformationModal
                                show={showCompletedModal}
                                onCancel={handleOnClickCancel}
                                text="Load marked complete successfully!"
                            />
                            <ForbiddenAccessModal show={showForbiddenModal} onCancel={handleOnClickCancel} />
                        </div>
                    );
                } else {
                    return <div></div>;
                }
            }}
        </Query>
    );
};

export default LoadProgress;

